/* TEMPORARY — Epstein cleanup diagnostics panel (Phase 1).
 * Remove this file + tab + api helpers after cleanup is complete.
 */
import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, Archive, CheckCircle2, AlertTriangle, Image as ImageIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  efAdminCleanupImportsStale, efAdminCleanupImportsArchive,
  efAdminCleanupNonPublic, efAdminCleanupBundleCoverage,
  efAdminCleanupAnchorStatus, efAdminCleanupAnchorSetImage,
  efAdminCleanupSourceAttachments,
} from "../../lib/api";

const Box = ({ title, children, "data-testid": tid }) => (
  <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-3" data-testid={tid}>
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// {title}</div>
    {children}
  </div>
);

const Stat = ({ k, v, intent }) => (
  <div className="border border-[#2D2A26] bg-[#0a0907] px-3 py-2 inline-block mr-2 mb-2">
    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">{k}</div>
    <div className={"font-display text-xl " + (
      intent === "ok" ? "text-[#5DD4B3]" :
      intent === "warn" ? "text-[#E0A526]" :
      intent === "danger" ? "text-[#D92D20]" : "text-[#E4DFD3]")}>{v}</div>
  </div>
);

export const AdminEpsteinCleanup = ({ token, userToken = null }) => {
  const [stale, setStale] = useState(null);
  const [nonPub, setNonPub] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [anchor, setAnchor] = useState(null);
  const [attachments, setAttachments] = useState(null);
  const [selectedStale, setSelectedStale] = useState(new Set());
  const [busy, setBusy] = useState({});
  const [imgUrl, setImgUrl] = useState("");
  const [imgField, setImgField] = useState("photo_url");
  const [imgOverwrite, setImgOverwrite] = useState(false);

  const run = (label, fn, setter) => async () => {
    setBusy(b => ({ ...b, [label]: true }));
    try { setter(await fn()); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    finally { setBusy(b => ({ ...b, [label]: false })); }
  };

  const loadAll = async () => {
    await Promise.all([
      run("stale", () => efAdminCleanupImportsStale(token, userToken), setStale)(),
      run("nonpub", () => efAdminCleanupNonPublic(token, userToken), setNonPub)(),
      run("coverage", () => efAdminCleanupBundleCoverage(token, null, userToken), setCoverage)(),
      run("anchor", () => efAdminCleanupAnchorStatus(token, userToken), setAnchor)(),
      run("attach", () => efAdminCleanupSourceAttachments(token, userToken), setAttachments)(),
    ]);
  };

  useEffect(() => { loadAll(); }, []);

  const toggleStale = (id) => {
    const n = new Set(selectedStale);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelectedStale(n);
  };

  const archiveSelected = async () => {
    if (selectedStale.size === 0) { toast.error("Select rows first"); return; }
    if (!window.confirm(`Archive ${selectedStale.size} stale import rows? (Recoverable.)`)) return;
    setBusy(b => ({ ...b, archive: true }));
    try {
      const r = await efAdminCleanupImportsArchive(token,
        { batch_ids: [...selectedStale], reason: "post-restore cleanup" }, userToken);
      toast.success(`Archived ${r.archived} rows`);
      setSelectedStale(new Set());
      await run("stale", () => efAdminCleanupImportsStale(token, userToken), setStale)();
    } catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
    finally { setBusy(b => ({ ...b, archive: false })); }
  };

  const setAnchorImage = async () => {
    if (!imgUrl.match(/^https?:\/\//i)) { toast.error("Need a full http(s) URL"); return; }
    setBusy(b => ({ ...b, setImage: true }));
    try {
      const r = await efAdminCleanupAnchorSetImage(token,
        { image_url: imgUrl.trim(), field: imgField, confirm_overwrite: imgOverwrite }, userToken);
      toast.success(`Set ${r.field}`);
      setImgUrl(""); setImgOverwrite(false);
      await run("anchor", () => efAdminCleanupAnchorStatus(token, userToken), setAnchor)();
    } catch (e) { toast.error(e?.response?.data?.detail || "Set image failed"); }
    finally { setBusy(b => ({ ...b, setImage: false })); }
  };

  const fieldCls = "w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-[11px] text-[#E4DFD3]";

  return (
    <div data-testid="ef-admin-cleanup-panel">
      <div className="border border-[#E0A526]/40 bg-[#1a1408] px-3 py-2 mb-3 flex items-start gap-2">
        <AlertTriangle size={14} className="text-[#E0A526] mt-0.5 shrink-0" />
        <div className="text-[11px] text-[#E4DFD3] leading-relaxed">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E0A526]">TEMPORARY ·</span>{" "}
          Post-restore cleanup tooling. Diagnostics + targeted fixes. Remove after the production cleanup is verified.
        </div>
      </div>

      <button onClick={loadAll} data-testid="ef-cleanup-refresh"
        className="mb-3 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-2">
        <RefreshCw size={11} /> Refresh all
      </button>

      {/* 4. Anchor status + image set */}
      <Box title="ANCHOR STATUS · IMAGE FIX" data-testid="ef-cleanup-anchor">
        {!anchor && <div className="text-[#757065] font-mono text-[10px]">// loading…</div>}
        {anchor && (
          <>
            <div className="grid sm:grid-cols-2 gap-3 mb-2">
              <pre className="font-mono text-[10px] text-[#E4DFD3] whitespace-pre-wrap break-all">
{JSON.stringify(anchor.anchor, null, 2)}
              </pre>
              <div className="font-mono text-[10px] space-y-2">
                {["image_probe", "photo_probe", "thumb_probe"].map(k => anchor[k] && (
                  <div key={k} className="border border-[#2D2A26] p-2">
                    <div className="text-[#757065] text-[9px] uppercase tracking-[0.25em]">{k}</div>
                    <div className={anchor[k].ok ? "text-[#5DD4B3]" : "text-[#D92D20]"}>
                      {anchor[k].ok ? "✓ loads" : "✗ broken"} · {anchor[k].http || anchor[k].error}
                    </div>
                    <div className="text-[#9FA8D4] break-all">{anchor[k].url}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-[#2D2A26] pt-2 mt-2 space-y-2">
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
                set anchor image via URL paste (phase 1 — phone gallery upload comes next):
              </div>
              <input value={imgUrl} onChange={e => setImgUrl(e.target.value)}
                data-testid="ef-cleanup-anchor-url"
                placeholder="https://… (paste a full image URL)" className={fieldCls} />
              <div className="flex flex-wrap gap-2 items-center">
                <select value={imgField} onChange={e => setImgField(e.target.value)}
                  data-testid="ef-cleanup-anchor-field" className={fieldCls + " max-w-xs"}>
                  <option value="photo_url">photo_url</option>
                  <option value="image_url">image_url</option>
                  <option value="thumbnail_url">thumbnail_url</option>
                </select>
                <label className="font-mono text-[10px] text-[#A39E93] flex items-center gap-1">
                  <input type="checkbox" checked={imgOverwrite} onChange={e => setImgOverwrite(e.target.checked)} />
                  confirm overwrite if already set
                </label>
                <button onClick={setAnchorImage} disabled={busy.setImage || !imgUrl}
                  data-testid="ef-cleanup-anchor-save"
                  className="bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-1">
                  {busy.setImage ? <Loader2 size={10} className="animate-spin" /> : <ImageIcon size={10} />}
                  Set anchor image
                </button>
              </div>
            </div>
          </>
        )}
      </Box>

      {/* 3. Bundle coverage */}
      <Box title="BUNDLE COVERAGE · is every bundled item in the public graph?" data-testid="ef-cleanup-coverage">
        {!coverage && <div className="text-[#757065] font-mono text-[10px]">// loading…</div>}
        {coverage && coverage.coverage.map((c, i) => (
          <div key={i} className="border border-[#2D2A26] p-2 mb-2">
            <div className="font-mono text-[10px] text-[#E4DFD3] mb-1">{c.file}</div>
            {c.summary && (
              <div className="font-mono text-[10px] text-[#A39E93]">
                sources public: <b className="text-[#E4DFD3]">{c.summary.sources_public}/{c.summary.sources_total}</b>{" · "}
                entities public: <b className="text-[#E4DFD3]">{c.summary.entities_public}/{c.summary.entities_total}</b>{" · "}
                relationships public: <b className="text-[#E4DFD3]">{c.summary.rels_public}/{c.summary.rels_total}</b>
              </div>
            )}
            <details className="mt-1">
              <summary className="font-mono text-[9px] text-[#757065] cursor-pointer">expand misses only</summary>
              {["sources", "entities", "relationships"].map(t => {
                const misses = (c[t] || []).filter(x => !x.found_in_public);
                if (!misses.length) return null;
                return (
                  <div key={t} className="mt-1">
                    <div className="font-mono text-[9px] text-[#D92D20]">{t} missing from public ({misses.length}):</div>
                    {misses.slice(0, 30).map((m, j) => (
                      <div key={j} className="font-mono text-[9px] text-[#A39E93] break-all">
                        ~ {m.ref || m.from || ""} {m.name || m.title || m.type || ""} {!m.found_in_db && "(not in DB at all)"}
                      </div>
                    ))}
                  </div>
                );
              })}
            </details>
          </div>
        ))}
      </Box>

      {/* 5. Source attachments */}
      <Box title="SOURCE ATTACHMENTS · restored entities spot-check" data-testid="ef-cleanup-attachments">
        {!attachments && <div className="text-[#757065] font-mono text-[10px]">// loading…</div>}
        {attachments && (
          <>
            <Stat k="restored entities" v={attachments.total} />
            <Stat k="anomalies" v={attachments.anomaly_count}
              intent={attachments.anomaly_count ? "warn" : "ok"} />
            <details>
              <summary className="font-mono text-[9px] text-[#757065] cursor-pointer">expand list</summary>
              <table className="w-full font-mono text-[10px] mt-2">
                <thead className="text-[#757065]">
                  <tr><th className="text-left">name</th><th>sources</th><th>anomaly</th><th>first source</th></tr>
                </thead>
                <tbody>
                  {attachments.entities.map(e => (
                    <tr key={e.id} className={e.anomaly ? "text-[#E0A526]" : "text-[#E4DFD3]"}>
                      <td className="pr-2">{e.name}</td>
                      <td className="text-center">{e.source_count}</td>
                      <td className="text-center">{e.anomaly || "—"}</td>
                      <td className="text-[#9FA8D4] break-all">
                        {e.first_sources?.[0]?.publisher} · {e.first_sources?.[0]?.url?.slice(0, 60)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </>
        )}
      </Box>

      {/* 2. Non-public entities */}
      <Box title="NON-PUBLIC ENTITIES · DRAFT vs PUBLIC editorial decisions" data-testid="ef-cleanup-nonpub">
        {!nonPub && <div className="text-[#757065] font-mono text-[10px]">// loading…</div>}
        {nonPub && (
          <>
            <Stat k="non-public total" v={nonPub.total}
              intent={nonPub.total > 0 ? "warn" : "ok"} />
            <details>
              <summary className="font-mono text-[9px] text-[#757065] cursor-pointer">expand list</summary>
              <table className="w-full font-mono text-[10px] mt-2">
                <thead className="text-[#757065]">
                  <tr><th className="text-left">name</th><th>kind</th><th>sources</th><th>img</th><th>batch</th><th>lifecycle</th></tr>
                </thead>
                <tbody>
                  {nonPub.entities.map(e => (
                    <tr key={e.id} className="text-[#E4DFD3]">
                      <td className="pr-2">{e.name}</td>
                      <td>{e.kind}</td>
                      <td className="text-center">{e.source_refs_count}</td>
                      <td className="text-center">{e.has_image ? "✓" : "—"}</td>
                      <td className="text-[#757065]">{e.batch_origin || "—"}</td>
                      <td className="text-[#E0A526]">{e.lifecycle_status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
            <div className="font-mono text-[10px] text-[#A39E93] mt-2">
              Walk this list via the existing Lifecycle tab → Review bucket to promote/reject.
            </div>
          </>
        )}
      </Box>

      {/* 1. Stale imports archive */}
      <Box title="STALE IMPORT BATCHES · archive (recoverable)" data-testid="ef-cleanup-stale">
        {!stale && <div className="text-[#757065] font-mono text-[10px]">// loading…</div>}
        {stale && (
          <>
            <Stat k="stale candidates" v={stale.stale_count}
              intent={stale.stale_count ? "warn" : "ok"} />
            <Stat k="total rows" v={stale.total_rows} />
            <button onClick={archiveSelected} disabled={busy.archive || selectedStale.size === 0}
              data-testid="ef-cleanup-archive-selected"
              className="bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-1 ml-2">
              {busy.archive ? <Loader2 size={10} className="animate-spin" /> : <Archive size={10} />}
              Archive selected ({selectedStale.size})
            </button>
            <details className="mt-2">
              <summary className="font-mono text-[9px] text-[#757065] cursor-pointer">expand list ({stale.stale.length} rows)</summary>
              <table className="w-full font-mono text-[10px] mt-2">
                <thead className="text-[#757065]">
                  <tr><th></th><th className="text-left">id</th><th>label</th><th>status</th><th>created</th></tr>
                </thead>
                <tbody>
                  {stale.stale.map(r => (
                    <tr key={r.id} className="text-[#E4DFD3]">
                      <td className="text-center">
                        <input type="checkbox" data-testid={`ef-cleanup-stale-${r.id}`}
                          checked={selectedStale.has(r.id)}
                          onChange={() => toggleStale(r.id)} />
                      </td>
                      <td className="pr-2 text-[#9FA8D4]">{r.id?.slice(0, 16)}…</td>
                      <td>{r.label || r.title || "—"}</td>
                      <td className="text-[#E0A526]">{r.status}</td>
                      <td className="text-[#757065]">{r.created_at?.slice(0, 16) || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </>
        )}
      </Box>
    </div>
  );
};

export default AdminEpsteinCleanup;
